import { useEffect, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@frontdoor/ui/components/alert';
import { Button } from '@frontdoor/ui/components/button';
import { Field, FieldError, FieldLabel } from '@frontdoor/ui/components/field';
import { Input } from '@frontdoor/ui/components/input';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { format, parse } from 'date-fns';
import { CircleAlert, Loader2 } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { useOutletContext, useParams, useSearchParams } from 'react-router';

import { type TrackingVerifyPayload, trackingV4VerifyAndGetTracking } from '@/api/generated';
import { trackingV4VerifyAndGetTrackingMutation } from '@/api/generated/@tanstack/react-query.gen';
import { DateInput } from '@/components/DateInput.tsx';
import type { TrackingOutletContext } from '@/components/layout/TrackingLayout.tsx';
import { ApiError } from '@/lib/errors.ts';
import { isPostHogEnabled, posthog } from '@/lib/posthog.tsx';
import { type VerificationFormData, verificationSchema } from '@/lib/schemas.ts';

import { StatusPage } from './StatusPage.tsx';

function trackEvent(event: string, properties?: Record<string, unknown>) {
  if (isPostHogEnabled) {
    posthog.capture(event, properties);
  }
}

const REJECTED_ALERT = {
  title: 'Verification failed',
  description: "We couldn't verify your identity. Please check your information and try again.",
};

const UNEXPECTED_ALERT = {
  title: 'Something went wrong',
  description: "We couldn't load this order right now. Please try again in a moment.",
};

// The API answers a bad name+DOB with a 400. Every other failure — a 5xx, a dropped
// connection, a response that fails schema validation — is ours, so it must not tell the
// patient their identity was wrong.
function isRejectedVerification(error: unknown) {
  return error instanceof ApiError && error.status === 400;
}

function errorAlert(verifyError: unknown, redeemError: unknown) {
  if (isRejectedVerification(verifyError)) return REJECTED_ALERT;
  if (verifyError ?? redeemError) return UNEXPECTED_ALERT;
  return null;
}

export function VerificationPage() {
  const { publicId } = useParams<{ publicId: string }>();
  const [searchParams] = useSearchParams();
  const mocksEnabled = import.meta.env.VITE_ENABLE_MOCKS === 'true';
  const mockStatus = mocksEnabled ? searchParams.get('status') : null;
  const mockBypass = mocksEnabled && searchParams.has('bypass');
  const [verifiedOrder, setVerifiedOrder] = useState<TrackingVerifyPayload | null>(null);
  const setHeaderPatientInfo = useOutletContext<TrackingOutletContext | null>()
    ?.setHeaderPatientInfo;

  const verifyMutation = useMutation(trackingV4VerifyAndGetTrackingMutation());

  // A query (not a mutation) so the redeem dedupes across StrictMode remounts.
  const redeemQuery = useQuery({
    queryKey: ['tracking-redeem', publicId, mockStatus, mockBypass],
    queryFn: async () => {
      const { data } = await trackingV4VerifyAndGetTracking({
        body: {},
        path: { public_id: publicId! },
        credentials: 'include',
      });
      // No valid ticket: 200 with no payload -> fall back to the name+DOB form.
      return data.payload ?? null;
    },
    enabled: Boolean(publicId),
    retry: false,
    staleTime: Infinity,
    // The ticket is single-use, so don't keep the result past the view; revisits re-redeem.
    gcTime: 0,
  });

  const orderData = verifiedOrder ?? redeemQuery.data ?? null;

  useEffect(() => {
    setVerifiedOrder(null);
  }, [publicId, mockStatus, mockBypass]);

  useEffect(() => {
    // Only a real redeem (payload present) is a handoff view.
    if (redeemQuery.isSuccess && redeemQuery.data) {
      trackEvent('tracking_handoff_status_viewed', { public_id: publicId });
    }
  }, [redeemQuery.isSuccess, redeemQuery.data, publicId]);

  useEffect(() => {
    setHeaderPatientInfo?.(
      orderData
        ? {
            patientName: `${orderData.order.patient.first_name} ${orderData.order.patient.last_name}`,
            orderId: String(orderData.order.order_id),
          }
        : null
    );
  }, [orderData, setHeaderPatientInfo]);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<VerificationFormData>({
    resolver: zodResolver(verificationSchema),
    defaultValues: {
      last_name: '',
      date_of_birth: '',
    },
  });

  const onSubmit = (data: VerificationFormData) => {
    if (!publicId) return;

    trackEvent('tracking_verification_submitted', { public_id: publicId });

    const apiDob = format(parse(data.date_of_birth, 'MM/dd/yyyy', new Date()), 'yyyy-MM-dd');

    verifyMutation.mutate(
      {
        body: { last_name: data.last_name, date_of_birth: apiDob },
        path: { public_id: publicId },
      },
      {
        onSuccess: (response) => {
          if (!response.payload) return;
          trackEvent('tracking_verification_success', { public_id: publicId });
          setVerifiedOrder(response.payload);
        },
        onError: (error) => {
          trackEvent('tracking_verification_failed', {
            public_id: publicId,
            reason: isRejectedVerification(error) ? 'rejected' : 'unexpected',
          });
        },
      }
    );
  };

  const errorBanner = errorAlert(verifyMutation.error, redeemQuery.error);

  if (orderData) {
    return <StatusPage data={orderData} publicId={publicId!} />;
  }

  if (redeemQuery.isLoading) {
    return (
      <div className="mx-auto flex w-full max-w-[892px] flex-1 flex-col px-4 pt-12 md:px-8 md:pt-16">
        <div
          className="mx-auto flex w-full max-w-md flex-col items-center py-16"
          role="status"
          aria-busy="true"
        >
          <Loader2 className="size-8 animate-spin" />
          <span className="sr-only">Loading</span>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-[892px] flex-1 flex-col px-4 pt-12 md:px-8 md:pt-16">
      <div className="mx-auto w-full max-w-md">
        <div className="mb-8 flex flex-col gap-2">
          <p className="text-base">Track an order</p>
          <h1 className="font-serif text-[32px] leading-tight">
            Verify patient info to see order details
          </h1>
        </div>

        {errorBanner && (
          <Alert className="mb-6 border-none bg-rose-200 text-rose-700">
            <CircleAlert className="size-4" />
            <AlertTitle>{errorBanner.title}</AlertTitle>
            <AlertDescription className="text-rose-700/90">
              {errorBanner.description}
            </AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
          <Field data-invalid={!!errors.last_name}>
            <FieldLabel htmlFor="last_name">Patient last name</FieldLabel>
            <Controller
              name="last_name"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  id="last_name"
                  placeholder="Last name"
                  autoComplete="family-name"
                  aria-invalid={!!errors.last_name}
                  aria-describedby={errors.last_name ? 'last_name-error' : undefined}
                />
              )}
            />
            <FieldError id="last_name-error" errors={errors.last_name ? [errors.last_name] : []} />
          </Field>

          <Field data-invalid={!!errors.date_of_birth}>
            <FieldLabel htmlFor="date_of_birth">Patient date of birth</FieldLabel>
            <Controller
              name="date_of_birth"
              control={control}
              render={({ field }) => (
                <DateInput
                  {...field}
                  id="date_of_birth"
                  aria-invalid={!!errors.date_of_birth}
                  aria-describedby={errors.date_of_birth ? 'date_of_birth-error' : undefined}
                />
              )}
            />
            <FieldError
              id="date_of_birth-error"
              errors={errors.date_of_birth ? [errors.date_of_birth] : []}
            />
          </Field>

          <Button type="submit" className="mt-2 w-full" disabled={verifyMutation.isPending}>
            {verifyMutation.isPending ? (
              <>
                <Loader2 className="animate-spin" />
                Verifying...
              </>
            ) : (
              'Continue'
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
